import React, { useState, useRef, useEffect } from 'react';
import '../css/selectField.css';
import { ChevronIcon, SearchIcon } from '../../../../../assets-module/asset-configuration/icon/iconExporter/iconExporter';

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface SelectFieldProps {
  id?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  label?: string;
  options: SelectOption[];
  onChange?: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  searchable?: boolean;
  assistiveText?: string;
  error?: boolean;
  errorText?: string;
  warning?: boolean;
}

const SelectField: React.FC<SelectFieldProps> = ({
  id,
  name,
  value,
  placeholder,
  label,
  options,
  onChange,
  onFocus,
  onBlur,
  required = false,
  disabled = false,
  className = '',
  searchable = false,
  assistiveText,
  error = false,
  errorText,
  warning = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);
  const displayLabel = label || placeholder;
  const hasValue = value && value.length > 0;
  const showFloatingLabel = isOpen || hasValue;

  const hasAsterisk = displayLabel?.includes('*');
  const labelText = hasAsterisk ? displayLabel?.replace(' *', '').replace('*', '') : displayLabel;

  const renderLabel = () => {
     /* istanbul ignore next */
    if (!displayLabel) return null;
    return (
      <>
        {labelText}
        {hasAsterisk && <span className="scl--web-component-select-field-required-asterisk"> *</span>}
      </>
    );
  };

  const filteredOptions = searchable
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionValue: string) => {
    if (onChange) {
      onChange(optionValue);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div
      ref={containerRef}
      className={`scl--web-component-select-field-container ${className}`}
    >
      <div //NOSONAR
        className={`scl--web-component-select-field-input-plate ${error ? 'error' : ''} ${warning ? 'warning' : ''} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
      >
        {displayLabel && (
          <label className={`scl--web-component-select-field-label ${showFloatingLabel ? 'floating' : ''}`}>
            {renderLabel()}
          </label>
        )}

        <div className="scl--web-component-select-field-input-wrapper">
          <div className="scl--web-component-select-field-value-wrapper">
            <div className="scl--web-component-select-field-text-wrapper">
              {hasValue && (
                <span className="scl--web-component-select-field-text">
                  {selectedOption?.icon && (
                    <span className="scl--web-component-select-field-selected-icon">
                      {selectedOption.icon}
                    </span>
                  )}
                  {selectedOption?.label}
                </span>
              )}
            </div>

            <ChevronIcon className="scl--web-component-select-field-chevron-icon"/>
          </div>
        </div>
      </div>

      <div className={`scl--web-component-select-field-dropdown ${isOpen ? 'open' : ''}`}>
        {searchable && (
          <div className="scl--web-component-select-field-search">
            <SearchIcon className='scl--web-component-select-field-search-icon' />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              className="scl--web-component-select-field-search-input"
              onClick={(e) => e.stopPropagation()}
              autoFocus={isOpen}
              autoComplete='off'
            />
          </div>
        )}

        <div className="scl--web-component-select-field-options">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div //NOSONAR
                key={option.value}
                className={`scl--web-component-select-field-option ${value === option.value ? 'selected' : ''}`}
                onClick={() => handleSelect(option.value)}
              >
                {option.icon && (
                  <span className="scl--web-component-select-field-option-icon">
                    {option.icon}
                  </span>
                )}
                <span className="scl--web-component-select-field-option-label">
                  {option.label}
                </span>
              </div>
            ))
          ) : (
            <div className="scl--web-component-select-field-no-options">
              No options found
            </div>
          )}
        </div>
      </div>

      {(assistiveText || errorText) && (
        <div className={`scl--web-component-select-field-assistive ${error ? 'error' : ''}`}>
          {error && errorText ? errorText : assistiveText}
        </div>
      )}

      {/* Hidden select for form submission */}
      <select
        id={id}
        name={name}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        required={required}
        disabled={disabled}
        style={{ display: 'none' }}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default SelectField;